import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit"
import type { RootState } from "@/store"
import type {
  BankDetailsDto,
  EmployeeOnboardingDetailsDto,
  ValidationErrorsDto,
} from "@/modules/employee/employee/dto/employeeDetails"
import { onboardingService } from "@/modules/employee/infrastructure/employeeService"

function getErrorMessage(err: unknown, fallback: string) {
  if (typeof err === "object" && err !== null) {
    const maybeResponse = (err as { response?: unknown }).response
    if (typeof maybeResponse === "object" && maybeResponse !== null) {
      const maybeData = (maybeResponse as { data?: unknown }).data
      if (typeof maybeData === "object" && maybeData !== null) {
        const maybeMessage = (maybeData as { message?: unknown }).message
        if (typeof maybeMessage === "string") return maybeMessage
      }
    }
  }

  if (err instanceof Error && err.message) return err.message
  return fallback
}

function parseComplexFields(
  data: EmployeeOnboardingDetailsDto,
): EmployeeOnboardingDetailsDto {
  return {
    ...data,
    address:
      typeof data.address === "string"
        ? JSON.parse(data.address || "{}")
        : data.address,
    bankDetails:
      typeof data.bankDetails === "string"
        ? JSON.parse(data.bankDetails || "{}")
        : data.bankDetails,
  }
}

interface EmployeeOnboardingState {
  onboardingData: EmployeeOnboardingDetailsDto
  isLoading: boolean
  isUpdatingData: boolean
  isSubmitting: boolean
  errors: ValidationErrorsDto
  submitError: string | null
  submitResult?: {
    id: string
    status: string
    submittedAt: string
    message: string
  }
}

const initialState: EmployeeOnboardingState = {
  onboardingData: {
    // Onboarding portion
    onboardingId: "",
    onboardingStatus: "",
    seenWelcome: false,
    inviteSent: false,
    notes: [],
    requiredDocuments: [],
    onboardingCreatedAt: "",
    onboardingUpdatedAt: "",
    onboardingStartDate: "",
    latest: true,
    previousOnboardingId: "",

    // Employee portion
    employeeId: "",
    identityType: "",
    identityNumber: "",
    firstName: "",
    lastName: "",
    employeeStatus: "",
    email: "",
    phone: "",
    position: "",
    employeeStartDate: "",
    dateOfBirth: "",
    gender: "",
    taxNumber: "",
    raceGroup: "",
    maritalStatus: "",
    emergencyContactName: "",
    emergencyContactPhone: "",
    employeeCreatedAt: "",
    employeeUpdatedAt: "",
    address: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
    },
    bankDetails: {
      accountNumber: "",
      bankName: "",
      branchCode: "",
      accountHolderName: "",
      accountType: "",
    },
    fieldFlags: [],
    documentsToBeSigned: [],

    // Organization details
    organizationId: "",
    organizationName: "",
  },
  isLoading: true,
  isSubmitting: false,
  isUpdatingData: false,
  errors: {},
  submitError: null,
}

interface OnboardingSubmitResponse {
  id: string
  status: string
  submittedAt: string
  message: string
}

interface SubmitOnboardingPayload {
  formData: EmployeeOnboardingDetailsDto
  requiredDocFiles?: Map<string, File>
  signedDocFiles?: Map<string, File>
}

function buildOnboardingFormData(
  formData: EmployeeOnboardingDetailsDto,
  requiredDocFiles?: Map<string, File>,
  signedDocFiles?: Map<string, File>,
): FormData {
  const submitData = new FormData()

  const emptyGuid = "00000000-0000-0000-0000-000000000000"
  const toGuid = (v: string) => (v && v !== "" ? v : emptyGuid)
  const toIsoDate = (v: string) => {
    if (!v || v === "") return new Date().toISOString()
    const d = new Date(v)
    return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString()
  }

  const onboardingStatusMap: Record<string, string> = {
    Rejected: "-1",
    Pending: "0",
    Started: "1",
    Submitted: "2",
    Approved: "3",
    Completed: "4",
  }
  const employeeStatusMap: Record<string, string> = {
    Cancelled: "-1",
    Pending: "0",
    Active: "1",
  }
  const identityTypeMap: Record<string, string> = {
    NationalId: "0",
    Passport: "1",
  }

  // Onboarding portion
  submitData.append("onboardingId", toGuid(formData.onboardingId))
  submitData.append(
    "onboardingStatus",
    onboardingStatusMap[formData.onboardingStatus] ?? "0",
  )
  submitData.append("seenWelcome", String(formData.seenWelcome))
  submitData.append("inviteSent", String(formData.inviteSent))
  submitData.append("workflowTemplateIds", formData.workflowTemplateIds ?? "")
  // Notes are now EmployeeFieldFlagDto[] and are read-only (managed by admins)
  // They should not be submitted by employees
  if (formData.requiredDocuments && formData.requiredDocuments.length > 0) {
    formData.requiredDocuments.forEach((doc, i) => {
      submitData.append(`requiredDocuments[${i}]`, doc)
    })
  }
  submitData.append(
    "onboardingCreatedAt",
    toIsoDate(formData.onboardingCreatedAt),
  )
  submitData.append(
    "onboardingUpdatedAt",
    toIsoDate(formData.onboardingUpdatedAt),
  )
  submitData.append(
    "onboardingStartDate",
    toIsoDate(formData.onboardingStartDate),
  )
  submitData.append("latest", String(formData.latest))
  submitData.append(
    "previousOnboardingId",
    toGuid(formData.previousOnboardingId),
  )

  // Employee portion
  submitData.append("employeeId", toGuid(formData.employeeId))
  submitData.append(
    "identityType",
    identityTypeMap[formData.identityType] ?? "0",
  )
  submitData.append("identityNumber", formData.identityNumber)
  submitData.append("firstName", formData.firstName)
  submitData.append("secondName", formData.secondName ?? "")
  submitData.append("lastName", formData.lastName)
  submitData.append(
    "employeeStatus",
    employeeStatusMap[formData.employeeStatus] ?? "0",
  )
  submitData.append("email", formData.email)
  submitData.append("phone", formData.phone)
  submitData.append("position", formData.position)
  submitData.append("employeeStartDate", toIsoDate(formData.employeeStartDate))
  if (formData.dateOfBirth && formData.dateOfBirth !== "") {
    submitData.append("dateOfBirth", toIsoDate(formData.dateOfBirth))
  }
  submitData.append("gender", formData.gender)
  submitData.append("taxNumber", formData.taxNumber)
  const raceGroupMap: Record<string, string> = {
    A: "0",
    C: "1",
    I: "2",
    W: "3",
  }
  if (formData.raceGroup) {
    submitData.append("raceGroup", raceGroupMap[formData.raceGroup] ?? "0")
  }
  const maritalStatusMap: Record<string, string> = {
    Single: "0",
    Married: "1",
    Divorced: "2",
    Widowed: "3",
    PermanentlySeparated: "4",
  }
  if (formData.maritalStatus) {
    submitData.append(
      "maritalStatus",
      maritalStatusMap[formData.maritalStatus] ?? "0",
    )
  }
  submitData.append("emergencyContactName", formData.emergencyContactName)
  submitData.append("emergencyContactPhone", formData.emergencyContactPhone)
  submitData.append("address", JSON.stringify(formData.address))
  submitData.append("bankDetails", JSON.stringify(formData.bankDetails))
  const accountTypeMap: Record<string, string> = {
    Cheque: "0",
    Savings: "1",
    Transmission: "2",
  }
  const bankDetailsObj =
    typeof formData.bankDetails === "string"
      ? ({} as BankDetailsDto)
      : formData.bankDetails
  if (bankDetailsObj.accountType) {
    submitData.append(
      "accountType",
      accountTypeMap[bankDetailsObj.accountType] ?? "0",
    )
  }
  submitData.append(
    "accountHolderName",
    typeof formData.bankDetails === "string"
      ? ""
      : formData.bankDetails.accountHolderName,
  )
  submitData.append("employeeCreatedAt", toIsoDate(formData.employeeCreatedAt))
  submitData.append("employeeUpdatedAt", toIsoDate(formData.employeeUpdatedAt))

  // Organization details
  submitData.append("organizationId", toGuid(formData.organizationId ?? ""))
  submitData.append("organizationName", formData.organizationName ?? "")

  // Required document files
  if (requiredDocFiles) {
    let index = 0
    requiredDocFiles.forEach((file, docType) => {
      submitData.append(
        `RequiredDocumentUploads[${index}].DocumentName`,
        docType,
      )
      submitData.append(`RequiredDocumentUploads[${index}].File`, file)
      index++
    })
  }

  // Signed document files
  if (signedDocFiles) {
    let index = 0
    signedDocFiles.forEach((file, originalDocId) => {
      submitData.append(
        `SignedDocuments[${index}].OriginalDocumentId`,
        originalDocId,
      )
      submitData.append(`SignedDocuments[${index}].SignedFile`, file)
      index++
    })
  }

  return submitData
}

export const submitOnboardingThunk = createAsyncThunk<
  OnboardingSubmitResponse,
  SubmitOnboardingPayload,
  { rejectValue: string }
>(
  "employeeOnboarding/submit",
  async (
    { formData, requiredDocFiles, signedDocFiles },
    { rejectWithValue },
  ) => {
    try {
      const submitData = buildOnboardingFormData(
        formData,
        requiredDocFiles,
        signedDocFiles,
      )
      return await onboardingService.submitEmployee(submitData)
    } catch (err: unknown) {
      return rejectWithValue(
        getErrorMessage(err, "Failed to submit onboarding"),
      )
    }
  },
)

export const fetchEmployeeOnboardingThunk = createAsyncThunk<
  EmployeeOnboardingDetailsDto,
  void,
  { rejectValue: string }
>("employeeOnboarding/fetchMe", async (_, { rejectWithValue }) => {
  try {
    return await onboardingService.fetchMyOnboardingData()
  } catch (err: unknown) {
    return rejectWithValue(
      getErrorMessage(err, "Failed to fetch employee record"),
    )
  }
})

export const fetchOnboardingThunk = createAsyncThunk<
  EmployeeOnboardingDetailsDto,
  void,
  { rejectValue: string }
>("employeeOnboarding/fetch", async (_, { rejectWithValue }) => {
  try {
    return await onboardingService.fetchEmployee()
  } catch (err: unknown) {
    return rejectWithValue(
      getErrorMessage(err, "Failed to fetch onboarding data"),
    )
  }
})

export const updateOnboardingThunk = createAsyncThunk<
  EmployeeOnboardingDetailsDto,
  SubmitOnboardingPayload,
  { rejectValue: string }
>(
  "employeeOnboarding/update",
  async (
    { formData, requiredDocFiles, signedDocFiles },
    { rejectWithValue },
  ) => {
    try {
      const submitData = buildOnboardingFormData(
        formData,
        requiredDocFiles,
        signedDocFiles,
      )
      return await onboardingService.updateEmployeeOnboardingData(submitData)
    } catch (err: unknown) {
      return rejectWithValue(
        getErrorMessage(err, "Failed to fetch onboarding data"),
      )
    }
  },
)

export const fetchEmployeeByIdThunk = createAsyncThunk<
  EmployeeOnboardingDetailsDto,
  string,
  { rejectValue: string }
>("employeeOnboarding/fetchById", async (id, { rejectWithValue }) => {
  try {
    return await onboardingService.fetchEmployeeById(id)
  } catch (err: unknown) {
    return rejectWithValue(getErrorMessage(err, "Failed to fetch employee"))
  }
})

const employeeOnboardingSlice = createSlice({
  name: "employeeOnboarding",
  initialState,
  reducers: {
    updateFormData(
      state,
      action: PayloadAction<Partial<EmployeeOnboardingDetailsDto>>,
    ) {
      state.onboardingData = { ...state.onboardingData, ...action.payload }
      // Clear errors for the section being updated
      const firstKey = Object.keys(action.payload)[0]
      if (firstKey === "address" || firstKey === "bankDetails") {
        state.errors[firstKey === "address" ? "address" : "bank"] = []
      } else if (
        ["idProof", "addressProof", "bankStatement"].includes(firstKey)
      ) {
        state.errors.documents = []
      } else {
        state.errors.profile = []
      }
    },
    setErrors(state, action: PayloadAction<ValidationErrorsDto>) {
      state.errors = action.payload
    },
    clearOnboardingError(state) {
      state.submitError = null
    },
    resetOnboardingState(state) {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitOnboardingThunk.pending, (state) => {
        state.isSubmitting = true
        state.submitError = null
      })
      .addCase(submitOnboardingThunk.fulfilled, (state, action) => {
        state.isSubmitting = false
        state.submitResult = action.payload
      })
      .addCase(submitOnboardingThunk.rejected, (state, action) => {
        state.isSubmitting = false
        state.submitError = action.payload ?? "Failed to submit onboarding"
      })

    builder
      .addCase(fetchEmployeeOnboardingThunk.pending, (state) => {
        state.isLoading = true
        state.submitError = null
      })
      .addCase(fetchEmployeeOnboardingThunk.fulfilled, (state, action) => {
        state.isLoading = false
        state.onboardingData = parseComplexFields(action.payload)
      })
      .addCase(fetchEmployeeOnboardingThunk.rejected, (state, action) => {
        state.isLoading = false
        state.submitError = action.payload ?? "Failed to fetch employee record"
      })

    builder
      .addCase(updateOnboardingThunk.pending, (state) => {
        state.isUpdatingData = true
        state.submitError = null
      })
      .addCase(updateOnboardingThunk.fulfilled, (state) => {
        state.isUpdatingData = false
        // Do not overwrite onboardingData with the save response — the client
        // state is already current. Replacing it with the API response would
        // re-trigger form syncing against server-formatted data (e.g. enum
        // fields as integers) and invalidate the step validity indicators.
      })
      .addCase(updateOnboardingThunk.rejected, (state, action) => {
        state.isUpdatingData = false
        state.submitError = action.payload ?? "Failed to fetch employee record"
      })

    builder
      .addCase(fetchOnboardingThunk.pending, (state) => {
        state.isLoading = true
        state.submitError = null
      })
      .addCase(fetchOnboardingThunk.fulfilled, (state, action) => {
        state.isLoading = false
        state.onboardingData = parseComplexFields(action.payload)
      })
      .addCase(fetchOnboardingThunk.rejected, (state, action) => {
        state.isLoading = false
        state.submitError = action.payload ?? "Failed to fetch onboarding data"
      })

    builder
      .addCase(fetchEmployeeByIdThunk.pending, (state) => {
        state.isLoading = true
        state.submitError = null
      })
      .addCase(fetchEmployeeByIdThunk.fulfilled, (state, action) => {
        state.isLoading = false
        state.onboardingData = parseComplexFields(action.payload)
      })
      .addCase(fetchEmployeeByIdThunk.rejected, (state, action) => {
        state.isLoading = false
        state.submitError = action.payload ?? "Failed to fetch employee"
      })
  },
})

export const {
  updateFormData,
  setErrors,
  clearOnboardingError,
  resetOnboardingState,
} = employeeOnboardingSlice.actions

export default employeeOnboardingSlice.reducer

// Selectors
export const selectOnboardingData = (state: RootState) =>
  state.employeeDetails.onboardingData
export const selectOnboardingIsLoading = (state: RootState) =>
  state.employeeDetails.isLoading
export const selectOnboardingIsSubmitting = (state: RootState) =>
  state.employeeDetails.isSubmitting
export const selectOnboardingIsUpdatingData = (state: RootState) =>
  state.employeeDetails.isUpdatingData
export const selectOnboardingErrors = (state: RootState) =>
  state.employeeDetails.errors
export const selectOnboardingSubmitError = (state: RootState) =>
  state.employeeDetails.submitError
export const selectOnboardingSubmitResult = (state: RootState) =>
  state.employeeDetails.submitResult
